import { ChangeDetectionStrategy, Component, inject, OnDestroy, output, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import type { CsrfMissingError } from '@webapp/auth/interceptors/xsrf.interceptor';
import { ButtonComponent, InputErrorComponent, ModalComponent } from '@webapp/ui';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import { firstValueFrom } from 'rxjs';
import { ApiKeyExpiration } from '../../../interfaces/api-keys.interface';
import { ApiKeysService } from '../../../services/api-keys.service';

export const API_KEY_NAME_MAX_LENGTH = 100;

export type ApiKeyCreateView = 'form' | 'result' | 'confirmDiscard';

const trimmedRequired = (control: AbstractControl): ValidationErrors | null =>
	typeof control.value === 'string' && control.value.trim().length > 0 ? null : { required: true };

/**
 * Create flow for an API key. Self-contained on purpose: it calls the service directly and
 * keeps the plaintext key ONLY in the local `createdKey` signal, so the key never touches the
 * store, the container or any output. The parent renders it with `@if`, so closing destroys it.
 * Nothing here may be logged (no console.*).
 */
@Component({
	selector: 'app-api-key-create-modal',
	standalone: true,
	imports: [ReactiveFormsModule, ButtonComponent, InputErrorComponent, ModalComponent],
	templateUrl: './api-key-create-modal.component.html',
	styleUrl: './api-key-create-modal.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { '(document:keydown.escape)': 'onEscape()' },
})
export class ApiKeyCreateModalComponent implements OnDestroy {
	private readonly apiKeysService = inject(ApiKeysService);
	private readonly toastService = inject(ToastService);

	readonly nameMaxLength = API_KEY_NAME_MAX_LENGTH;

	/** `created` tells the parent whether the list must be refreshed. */
	closed = output<{ created: boolean }>();
	/** Backend answered 409: the parent closes the form and refreshes the list. */
	limitReached = output<void>();

	form = new FormGroup({
		name: new FormControl('', {
			nonNullable: true,
			validators: [trimmedRequired, Validators.maxLength(API_KEY_NAME_MAX_LENGTH)],
		}),
		expiration: new FormControl<ApiKeyExpiration>('90d', { nonNullable: true }),
	});

	view = signal<ApiKeyCreateView>('form');
	submitted = signal(false);
	submitting = signal(false);
	nameServerError = signal(false);
	/** The ONLY place the plaintext key lives. */
	createdKey = signal<string | null>(null);
	copied = signal(false);
	copyFailed = signal(false);

	private created = false;
	private finished = false;
	private destroyed = false;

	get nameLength(): number {
		return this.form.controls.name.value.length;
	}

	/** Bound to the activar-modal: the backdrop and the X only work in the plain form view. */
	get closable(): boolean {
		return this.view() === 'form' && !this.submitting();
	}

	onNameInput(): void {
		this.nameServerError.set(false);
	}

	async onSubmit(): Promise<void> {
		if (this.submitting() || this.view() !== 'form') {
			return;
		}
		this.submitted.set(true);
		this.form.markAllAsTouched();
		if (this.form.invalid) {
			return;
		}

		const { name, expiration } = this.form.getRawValue();
		this.submitting.set(true);
		this.form.disable({ emitEvent: false });
		this.nameServerError.set(false);
		try {
			const response = await firstValueFrom(this.apiKeysService.create({ name: name.trim(), expiration }));
			if (this.destroyed) {
				return;
			}
			// Keep only the key; the rest of the response is dropped.
			this.createdKey.set(response.key);
			this.created = true;
			this.view.set('result');
		} catch (error) {
			if (!this.destroyed) {
				this.handleError(error);
			}
		} finally {
			if (!this.destroyed) {
				this.form.enable({ emitEvent: false });
				this.submitting.set(false);
			}
		}
	}

	async copy(): Promise<void> {
		const key = this.createdKey();
		if (key === null) {
			return;
		}
		try {
			await navigator.clipboard.writeText(key);
			this.copyFailed.set(false);
			this.copied.set(true);
		} catch {
			// No logging and no toast: the field stays selectable for a manual copy.
			this.copied.set(false);
			this.copyFailed.set(true);
		}
	}

	/** Idempotent: activar-modal may emit visibleChange more than once. */
	onModalClose(): void {
		if (this.closable) {
			this.finish();
		}
	}

	onEscape(): void {
		if (this.finished || this.submitting()) {
			return;
		}
		switch (this.view()) {
			case 'form':
				this.finish();
				break;
			case 'result':
				this.requestDone();
				break;
			case 'confirmDiscard':
				// Esc backs out of the confirmation instead of discarding: losing the key is the
				// destructive action and must be explicit.
				this.keepKey();
				break;
		}
	}

	/** "Listo": asks first when the key was not copied. */
	requestDone(): void {
		if (this.view() !== 'result') {
			return;
		}
		if (this.copied()) {
			this.finish();
		} else {
			this.view.set('confirmDiscard');
		}
	}

	keepKey(): void {
		if (this.view() === 'confirmDiscard') {
			this.view.set('result');
		}
	}

	confirmDiscard(): void {
		if (this.view() === 'confirmDiscard') {
			this.finish();
		}
	}

	cancel(): void {
		if (this.view() === 'form' && !this.submitting()) {
			this.finish();
		}
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.createdKey.set(null);
	}

	private finish(): void {
		if (this.finished) {
			return;
		}
		this.finished = true;
		this.createdKey.set(null);
		this.closed.emit({ created: this.created });
	}

	private handleError(error: unknown): void {
		const status = (error as { status?: number } | null)?.status;
		if ((error as Partial<CsrfMissingError> | null)?.csrfMissing) {
			this.toastService.showToast(
				'Problema de seguridad',
				'No pudimos verificar tu sesión de forma segura. Actualizá la página e intentá de nuevo.',
				'error'
			);
		} else if (status === 400) {
			this.nameServerError.set(true);
		} else if (status === 409) {
			this.toastService.showToast('Atención', 'Máximo 3 keys activas', 'alert_warning');
			this.limitReached.emit();
		} else if (status === 401) {
			// Global expired-session flow handles it; no extra toast.
		} else {
			this.toastService.showToast('Error', 'No se pudo completar la acción. Intentá nuevamente.', 'error');
		}
	}
}
